import { ReactNode, forwardRef } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';

interface TableProps {
  children: ReactNode;
  className?: string;
}

export function Table({ children, className = '' }: TableProps) {
  return (
    <div className="overflow-x-auto">
      <table className={`min-w-full border-collapse ${className}`}>
        {children}
      </table>
    </div>
  );
}

interface TableHeadProps {
  children: ReactNode;
}

export function TableHead({ children }: TableHeadProps) {
  return (
    <thead className="bg-slate-50 border-b border-slate-200">
      {children}
    </thead>
  );
}

interface TableBodyProps {
  children: ReactNode;
}

export function TableBody({ children }: TableBodyProps) {
  return <tbody className="divide-y divide-slate-100 bg-white">{children}</tbody>;
}

interface TableRowProps {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ children, onClick, className = '', style }, ref) => {
    return (
      <tr
        ref={ref}
        onClick={onClick}
        style={style}
        className={`${onClick ? 'cursor-pointer hover:bg-slate-50' : ''} ${className}`}
      >
        {children}
      </tr>
    );
  }
);

export type TableSortDirection = 'asc' | 'desc';

interface TableHeaderProps {
  children: ReactNode;
  className?: string;
  /** Renders the label as a button that calls `onSort`; the server or page does the sorting. */
  sortable?: boolean;
  /** Current direction when this column is the active sort, otherwise `null`. */
  sortDirection?: TableSortDirection | null;
  onSort?: () => void;
}

export function TableHeader({
  children,
  className = '',
  sortable = false,
  sortDirection = null,
  onSort,
}: TableHeaderProps) {
  const base = `px-3 sm:px-4 py-2.5 text-left text-[11px] font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap ${className}`;

  if (!sortable) {
    return <th className={base}>{children}</th>;
  }

  const SortIcon = sortDirection === 'asc' ? ArrowUp : sortDirection === 'desc' ? ArrowDown : ArrowUpDown;
  const ariaSort = sortDirection === 'asc' ? 'ascending' : sortDirection === 'desc' ? 'descending' : 'none';

  return (
    <th className={base} aria-sort={ariaSort}>
      <button
        type="button"
        onClick={onSort}
        className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00A871]/40 rounded ${
          sortDirection ? 'text-slate-900' : ''
        }`}
      >
        {children}
        <SortIcon
          className={`w-3.5 h-3.5 shrink-0 ${sortDirection ? 'text-slate-700' : 'text-slate-300'}`}
          strokeWidth={2}
          aria-hidden="true"
        />
      </button>
    </th>
  );
}

interface TableCellProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  colSpan?: number;
}

export function TableCell({ children, className = '', style, colSpan }: TableCellProps) {
  return (
    <td
      className={`px-3 sm:px-4 py-3 text-[13px] text-slate-700 tabular-nums ${className}`}
      style={style}
      colSpan={colSpan}
    >
      {children}
    </td>
  );
}
